import { Box, CircularProgress, InputAdornment, LinearProgress, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { DropDown } from "components/dropdown";
import { Locations, SelectedLocations } from "../types";
import { LocationKind } from "types/geospatial";
import { LocationDataState } from "../hooks/useLocations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

const useStyles = makeStyles((theme: any) => ({
  filtersContainer: {
    display: "flex", flexDirection: "column", gap: "1rem"
  },
  loadingIndicator: {
    width: '100%',
  },
  dropdownListContainer: {
    display: "flex",
    gap: "1rem",
    flexDirection: "column"
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    minWidth: 0,
    '& > *': {
      flex: '1 1 0',
      minWidth: 0,
    },
    '& .MuiInputBase-root': {
      minWidth: 0,
      width: '100%',
    },
    '& input': {
      padding: theme.spacing(1),
      lineHeight: 1.4,
      minHeight: 0,
    },
    '& .MuiSelect-select': {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      '& > *': {
        flex: 'none',
        width: '100%',
        minWidth: 0,
      },
    },
  },
  searchIcon: {
    color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.grey[700],
    fontSize: '0.875rem',
  },
  searchBox: {
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderRadius: '5px',
      border: `1.2px solid ${theme.palette.border.line}`,
      padding: theme.spacing(1.2),
    },
    '& .MuiInputBase-inputHiddenLabel': { padding: 0 },
    '& ::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
    '& .MuiFilledInput-underline:before': {
      borderBottom: `1.2px solid ${theme.palette.border.line} !important`,
    }
  },
}));

type Props = {
  locationDataState: LocationDataState,
  locations: Locations,
  selectedLocations: SelectedLocations,
  onSelectLocation: (value: string, kind: LocationKind) => void,
  zctaQuery: string,
  onSearchZcta: (query: string) => void
}

export const Filters: FC<Props> = ({
  locationDataState,
  locations,
  selectedLocations,
  onSelectLocation,
  zctaQuery,
  onSearchZcta
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isLoading = Object.values(locationDataState).some(Boolean);

  const states = locations.state;
  const counties = locations.county;
  const core_areas = locations.core_area;
  const metropolitan_divisions = locations.metropolitan_division;
  const urban_areas = locations.urban_area;

  const selectedStateId = selectedLocations.state ? selectedLocations.state._id : "";
  const selectedCountyId = selectedLocations.county ? selectedLocations.county._id : "";
  const selectedCoreAreaId = selectedLocations.core_area ? selectedLocations.core_area._id : "";
  const selectedMetropolitanDivisionId = selectedLocations.metropolitan_division ? selectedLocations.metropolitan_division._id : "";
  const selectedUrbanAreaId = selectedLocations.urban_area ? selectedLocations.urban_area._id : "";

  return (
    <Box className={classes.filtersContainer}>
      {isLoading && <LinearProgress className={classes.loadingIndicator} aria-label={t('common:loading')} />}
      <Box className={classes.dropdownListContainer}>
        <Box className={classes.filterRow}>
          <DropDown
            options={states}
            optionValue="_id"
            optionText="name"
            onChange={(value) => onSelectLocation(value, "state")}
            defaultText={t('common:stateAndEquivalentLabel')}
            value={selectedStateId}
            disabled={isLoading}
            isDataLoading={locationDataState.isStateListLoading}
          />

          <DropDown
            options={counties}
            optionValue="_id"
            optionText="name"
            onChange={(value) => onSelectLocation(value, "county")}
            defaultText={t('common:countyLabel')}
            value={selectedCountyId}
            disabled={isLoading || selectedStateId === ""}
            isDataLoading={locationDataState.isCountyListLoading}
          />
        </Box>

        <Box className={classes.filterRow}>
          <DropDown
            options={core_areas}
            optionValue="_id"
            optionText="name"
            onChange={(value) => onSelectLocation(value, "core_area")}
            defaultText={t('common:coreAreaLabel')}
            value={selectedCoreAreaId}
            disabled={isLoading || selectedStateId === ""}
            isDataLoading={locationDataState.isCoreAreaListLoading}
          />

          <DropDown
            options={metropolitan_divisions}
            optionValue="_id"
            optionText="name"
            onChange={(value) => onSelectLocation(value, "metropolitan_division")}
            defaultText={t('common:metropolitanDivisionLabel')}
            value={selectedMetropolitanDivisionId}
            disabled={isLoading || selectedStateId === ""}
            isDataLoading={locationDataState.isMetropolitanDivisionListLoading}
          />

          <DropDown
            options={urban_areas}
            optionValue="_id"
            optionText="name"
            onChange={(value) => onSelectLocation(value, "urban_area")}
            defaultText={t('common:urbanAreaLabel')}
            value={selectedUrbanAreaId}
            disabled={isLoading || selectedStateId === ""}
            isDataLoading={locationDataState.isUrbanAreaListLoading}
          />
        </Box>
        <TextField
          hiddenLabel
          placeholder={t('common:searchByZipPlaceholder')}
          InputProps={{
            startAdornment: (
              locationDataState.isZctaLoading ? (
                <CircularProgress color="primary" size={12} />
              ) :
                (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faSearch} className={classes.searchIcon} />
                  </InputAdornment>
                )
            ),
          }}
          variant="filled"
          className={classes.searchBox}
          fullWidth
          disabled={isLoading}
          value={zctaQuery}
          onChange={(e) => {
            if (e.target.value === "" || /^(\d*-\d*|\d+)$/.test(e.target.value)) {
              onSearchZcta(e.target.value);
            }
          }}
        />
      </Box>
    </Box>
  );
};
